import { getQuestionnaires } from "@/features/appointments/utils";
import {
  getAnswersLength,
  getQuestionsLength,
} from "@/features/questionnaire/utils";
import {
  type AnswerData,
  type AppointmentStatus,
  type NeedAnalysisGroupItem,
  type Question,
  type QuestionObject,
  fetchAppointmentQuestions,
  saveB2BQuestionnaire,
  saveQuestionnaire,
  useB2BQuestionnaireAnswers,
  useOpportunityAppointments,
  useQuestionnaire,
  useQuestionnaireAnswers,
} from "@repo/api";
import { useSettings } from "@repo/ui";
import { hasItems, logError } from "@repo/utils";
import { useState } from "react";
import useSWR from "swr";

import { useActiveProject } from "./ActiveProject";

type Questionnaire = ReturnType<typeof getQuestionnaires>[number];

export interface BokaQuestionnaireData {
  questionnaires: Questionnaire[];
  selectedQuestionnaire: Questionnaire | undefined;
  onSelectionChange: (id: number) => void;
  questions: QuestionObject[] | undefined;
}

const isAnswered = (question: Question | undefined, answer?: AnswerData) => {
  if (!question || !answer) {
    return false;
  }

  if (question.answers) {
    return (answer.answerIds?.length ?? 0) > 0;
  }

  return (answer.answerFreeText ?? "").trim() !== "";
};

const getB2BQuestionStats = (
  groups: NeedAnalysisGroupItem[],
  templates: Question[],
  answers: AnswerData[]
) => {
  const b2bGroups = groups.filter((group) => group.group_name_key === "B2B");
  const b2bQuestionIds = b2bGroups.flatMap(
    (group) =>
      group.questions?.map((question) => question.question_id_key) ?? []
  );

  const totalQuestions = b2bQuestionIds.length;
  const totalAnswers = b2bQuestionIds.filter((questionIdKey) => {
    const templateQuestion = templates.find(
      (template) => template.question_id_key === questionIdKey
    );
    const answerData = answers.find(
      (answer) => answer.questionId === questionIdKey
    );

    return isAnswered(templateQuestion, answerData);
  }).length;

  return {
    totalAnswers,
    totalQuestions,
  };
};

const getAppointmentQuestionStats = (
  questions: { answer: { answer: string[] } }[] = []
) => {
  const totalQuestions = questions.length;
  const totalAnswers = questions.filter(
    (question) => (question.answer.answer?.length ?? 0) > 0
  ).length;

  return {
    totalAnswers,
    totalQuestions,
  };
};

export const useProjectQuestionnaire = (opportunityId?: number) => {
  const { opportunity } = useActiveProject();
  const effectiveOpportunityId = opportunityId ?? opportunity?.id;
  const { market, language } = useSettings();

  const settings = useQuestionnaire(market, language);
  const { data: answersData } = useQuestionnaireAnswers(effectiveOpportunityId);
  const { data: appointments } = useOpportunityAppointments(
    effectiveOpportunityId
  );
  const { data: b2bAnswersData } = useB2BQuestionnaireAnswers(
    opportunity?.customerProjects?.[0]?.id,
    opportunity?.partyId
  );

  const questionnaires = getQuestionnaires(appointments ?? []);

  // Fetch questions for all appointments that don't have inline questions
  const appointmentsToFetch = questionnaires.filter(
    (q) => !hasItems(q.questions)
  );
  const fetchIds = appointmentsToFetch.map((q) => q.id);

  const { data: allFallbackData } = useSWR(
    fetchIds.length > 0 ? ["appointment-questions", ...fetchIds] : null,
    async ([, ...ids]: [string, ...number[]]) => {
      return Promise.all(ids.map((id) => fetchAppointmentQuestions(id)));
    },
    { shouldRetryOnError: false }
  );

  const fallbackQuestionsMap = new Map(
    fetchIds.map((id, i) => [id, allFallbackData?.[i]])
  );

  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
    number | undefined
  >(undefined);

  const bokaSelectedQuestionnaire =
    questionnaires.find((q) => q.id === selectedQuestionnaireId) ??
    questionnaires[0];
  const bokaSelectedAppointmentHasQuestions = hasItems(
    bokaSelectedQuestionnaire?.questions
  );
  const bokaFallback =
    bokaSelectedQuestionnaire !== undefined
      ? fallbackQuestionsMap.get(bokaSelectedQuestionnaire.id)
      : undefined;
  const bokaQuestions: QuestionObject[] | undefined =
    bokaSelectedAppointmentHasQuestions
      ? bokaSelectedQuestionnaire?.questions
      : bokaFallback?.questions;
  const bokaTokenUrl =
    !bokaSelectedAppointmentHasQuestions &&
    hasItems(bokaQuestions) &&
    bokaFallback?.additionalQuestionsToken
      ? `https://www.ikea.com/${market}/${language}/appointment/questions/?token=${encodeURIComponent(
          bokaFallback.additionalQuestionsToken
        )}`
      : undefined;

  const selectedQuestionnaire = questionnaires[0];

  const [isSaving, setIsSaving] = useState(false);

  const allGroups = settings?.need_analysis?.groups ?? [];
  const nonB2BGroups = allGroups.filter(
    (group) => group.group_name_key !== "B2B"
  );
  const templates = settings?.templates?.questions ?? [];

  const needAnalysisAnswersLength = getAnswersLength(
    opportunity?.hfb ?? [],
    answersData?.answers ?? [],
    templates,
    nonB2BGroups,
    selectedQuestionnaire?.service
  );

  const needAnalysisQuestionsLength = getQuestionsLength(
    nonB2BGroups,
    opportunity?.hfb ?? [],
    selectedQuestionnaire?.service
  );

  const b2bStats = getB2BQuestionStats(
    allGroups,
    templates,
    b2bAnswersData?.answers ?? []
  );

  const appointmentStats = getAppointmentQuestionStats(
    questionnaires.flatMap((q) => {
      if (hasItems(q.questions)) {
        return q.questions ?? [];
      }
      return fallbackQuestionsMap.get(q.id)?.questions ?? [];
    })
  );

  const saveAnswer = async (answers: AnswerData[]) => {
    if (!effectiveOpportunityId) {
      return;
    }
    try {
      setIsSaving(true);
      for (const [i, answer] of answers.entries()) {
        if (answersData?.answers?.[i] !== answer) {
          await saveQuestionnaire(effectiveOpportunityId, answer.questionId, {
            ...answer,
            type: "NEED_ANALYSIS",
          });
        }
      }
    } catch (error) {
      logError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const saveB2bAnswers = async (b2bAnswers: AnswerData[]) => {
    if (!opportunity?.id) {
      return;
    }
    try {
      setIsSaving(true);
      await saveB2BQuestionnaire(
        opportunity.id,
        b2bAnswers,
        opportunity.customerProjects?.[0]?.id,
        opportunity.partyId
      );
    } catch (error) {
      logError(error);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    service: selectedQuestionnaire?.service,
    isSaving,
    saveAnswer,
    saveB2bAnswers,
    answersLength:
      needAnalysisAnswersLength +
      b2bStats.totalAnswers +
      appointmentStats.totalAnswers,
    questionsLength:
      needAnalysisQuestionsLength +
      b2bStats.totalQuestions +
      appointmentStats.totalQuestions,
    // Return all fetched data to avoid redundant fetching
    answersData,
    appointments,
    b2bAnswersData,
    settings,
    bokaQuestionnaire: {
      questionnaires,
      selectedQuestionnaire: bokaSelectedQuestionnaire,
      onSelectionChange: setSelectedQuestionnaireId,
      questions: bokaQuestions,
    } satisfies BokaQuestionnaireData,
    bokaTokenUrl,
    selectedStatus: bokaSelectedQuestionnaire?.status as
      AppointmentStatus | undefined,
  };
};
